import { Stack, Theme, Toolbar, Box } from '@mui/material';
import AppBar from '@mui/material/AppBar';

import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import useApp from '../../hooks/use-app.hook';
import Text from '../text';
import Breadcrumbs from './breadcrumbs';
import Loader from '../loader';
import UserMenu from './user-menu';
import SupportMenu from './support-menu';
import Chip from '../chip';
import useFeature from '@/hooks/use-feature.hook';
import AppLogo from '../app-logo';

interface Props {
  signedIn: boolean;
}

export const APP_HEADER_HEIGHT = 64; // affects height rules in app-header and app-sidebar. if you change this, you MUST change those

const useStyles = createUseStyles<string, { signedIn: boolean }>((theme: Theme) => ({
  header: ({ signedIn }) => ({
    backgroundColor: signedIn ? theme.palette.background.app : theme.palette.common.white,
    position: 'relative',
    zIndex: theme.zIndex.appBar,
    height: APP_HEADER_HEIGHT,
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  }),
  headerInner: {
    paddingRight: 24,
  },
  pageTitle: {
    flexGrow: 1,
    color: theme.palette.common.black,
  },
  logoLink: {
    '&:hover': {
      opacity: 1,
    },
  },
  userMenu: {
    padding: 0,
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '& > .MuiFab-root': {
      boxShadow: 'none',
      background: 'none',
    },
    '& .MuiSpeedDial-actions': {
      paddingBottom: 36,
    },
  },
  modeBadge: {
    backgroundColor: '#ebfeff !important',
    '& .MuiChip-label': {
      color: '#00adb6 !important',
    },
  },
}));

const AppHeader: FC<Props> = ({ signedIn }) => {
  const styles = useStyles({ signedIn });
  const { pageTitle, pageLoadState, pageMenu: PageMenuEl } = useApp();
  const { breadcrumb, plain } = pageTitle;
  const hasCrumbs = !!breadcrumb;
  const pageLoading = pageLoadState === 'loading';

  const { orgMode } = useFeature();
  const hasTrial = orgMode === 'trial';

  return (
    <AppBar className={styles.header} elevation={0}>
      <Toolbar className={styles.headerInner}>
        {!signedIn && (
          <Box width={150}>
            <a href="/" className={styles.logoLink}>
              <AppLogo />
            </a>
          </Box>
        )}

        {signedIn && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            className={styles.pageTitle}
            justifyContent="flex-start"
            width="100%"
          >
            {hasCrumbs && <Breadcrumbs breadcrumb={breadcrumb} pageTitle={plain} />}
            {!hasCrumbs && (
              <Text size="x-large" noWrap>
                {plain}
              </Text>
            )}
            {PageMenuEl}
            <Stack
              flexGrow={1}
              flexDirection="row"
              justifyContent="flex-end"
              gap={3}
              alignItems="center"
            >
              <Box width={50}>{pageLoading && <Loader />}</Box>

              {signedIn && hasTrial && (
                <Chip label="Trial Mode" color="success" className={styles.modeBadge} />
              )}

              {signedIn && (
                <>
                  <SupportMenu />
                  <UserMenu />
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
